import { Checkbox } from '@/components';
import ActivityTotalOptions from '@/components/ActivityTotalOptions';
import SportSelector from '@/components/SportSelector';
import { Sports } from '@/types';
import { currentYear, getDayYearNumberFromDate } from '@/utils/date';
import { dataTypeLabels, dataTypeUnits } from '@/utils/enum';
import { roundNumber } from '@/utils/maths';
import Highcharts, { Point, SeriesClickEventObject } from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HighStocks from 'highcharts/modules/stock';
import { useReducer, useRef, useState } from 'react';
import Head from '../components/Head';
import { NoData } from '../components/NoData';
import SkeletonLoader from '../components/SkeletonLoader';
import Total from '../components/Total';
import { useActivities } from '../hooks/useActivities';
import styles from '../style.module.css';
import {
  CustomActivity,
  getIsRace,
  getRidingActivities,
  getRunningActivities,
  getSwimmingActivities,
  parseAllActivities,
} from '../utils/activities';
import { getSufferScoreColor, titleStyle } from '../utils/common';
import Legend from './Legend';

const dummyActivity = (dayYear: number): CustomActivity => ({
  dayYear,
  distance: 0,
  moving_time: 0,
  elevation_gain: 0,
  suffer_score: 0,
  watts: 0,
  week: dayYear / 7,
  name: `${dayYear} activity`,
  id: dayYear,
  startTime: 0,
  hour: 0,
  day: 0,
  year: currentYear,
  sport_type: 'Run',
  workout_type: 0,
});

interface ExtendedPoint extends Point {
  custom: Record<string, number>;
}

const dataTypes = [
  'distance',
  'moving_time',
  'elevation_gain',
  'suffer_score',
] as const;
type DataTypes = (typeof dataTypes)[number];

const ActivityDayChart = () => {
  const chartComponentRef = useRef<HighchartsReact.RefObject>(null);
  const { activities, isLoading } = useActivities();
  const [sport, setSport] = useState<Sports>('running');
  const [dataType, setDataType] = useState<DataTypes>('distance');
  const [showDays, toggleShowDays] = useReducer((value) => !value, false);

  HighStocks(Highcharts);

  if (isLoading) {
    return <SkeletonLoader />;
  }

  if (!activities) {
    return <NoData>No activities</NoData>;
  }

  const handleRadioChangeSport = (event: React.ChangeEvent<HTMLInputElement>) =>
    setSport(event.target.value as Sports);

  const handleRadioChangeActivityTotal = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = event.target;
    setDataType(value as DataTypes);
  };

  const allActivities = activities.map(parseAllActivities);

  const [runningActivities, ridingActivities, swimmingActivities] = [
    getRunningActivities(allActivities),
    getRidingActivities(allActivities),
    getSwimmingActivities(allActivities),
  ];

  const getSelectedSportActivities = () => {
    switch (sport) {
      case 'running':
        return runningActivities;
      case 'cycling':
        return ridingActivities;
      case 'swimming':
        return swimmingActivities;
      default:
        return runningActivities;
    }
  };

  const selectedActivities = getSelectedSportActivities();

  const todayDate = new Date().toString();
  const nbDays = getDayYearNumberFromDate(todayDate);

  [...Array(nbDays).keys()].forEach((dayYear) => {
    dayYear = dayYear + 1;
    if (
      !selectedActivities.map((activity) => activity.dayYear).includes(dayYear)
    ) {
      showDays && selectedActivities.push(dummyActivity(dayYear));
    }
  });

  const sortedActivities = selectedActivities.sort(
    (a, b) => a.dayYear - b.dayYear
  );

  const sufferScores = sortedActivities.map((run) => run.suffer_score);
  const isRaces = sortedActivities.map((run) => getIsRace(run.workout_type));
  const colors = sufferScores
    .map((score) => getSufferScoreColor(score))
    .map((color, index) => (isRaces[index] ? color : color));

  const total = sortedActivities.reduce((acc, activity) => {
    return acc + activity[dataType];
  }, 0);

  const mean = roundNumber(total / sortedActivities.length);

  const options = {
    chart: {
      type: 'column',
      zoomType: 'x',
    },
    navigator: {
      enabled: true,
    },
    title: {
      text: '', // `Activités ${sport} en ${dataTypeUnits[dataType]}`
      style: titleStyle,
    },
    credits: {
      enabled: false,
    },
    xAxis: {
      title: {
        text: '',
      },
    },
    yAxis: {
      title: {
        text: dataTypeLabels[dataType],
      },
    },
    plotOptions: {
      column: {
        colorByPoint: true,
      },
    },
    legend: {
      enabled: false,
    },
    tooltip: {
      headerFormat: '{point.y} km<br/>',
      pointFormat: '<b>{point.custom.extraInformation}</b>',
    },
    colors,
    series: [
      {
        name: 'running',
        data: sortedActivities.map((activity) => ({
          y: activity[dataType],
          custom: {
            extraInformation: activity.name,
            id: activity.id,
          },
        })),
        dataLabels: {
          enabled: true,
          format: '{point.y:.1f}',
        },
        cursor: 'pointer',
        events: {
          click: (event: SeriesClickEventObject) => {
            const { id } = (event.point as ExtendedPoint).custom;
            const url = `https://www.strava.com/activities/${id}`;
            window.location.href = url;
          },
        },
      },
    ],
  };

  return (
    <div id="running-day">
      <Head title="Strava Dashboard - running-day" />
      <SportSelector
        sport={sport}
        handleRadioChangeSport={handleRadioChangeSport}
        showAll={false}
      />
      <ActivityTotalOptions
        activityTotal={dataType}
        handleRadioChangeActivityTotal={handleRadioChangeActivityTotal}
        dataTypes={dataTypes}
      />
      <div className={styles.total}>
        <Total total={total} animate={false} unit={dataTypeUnits[dataType]} />
        <div>
          <b>Moyenne:</b>
          {` ${mean} ${dataTypeUnits[dataType]} / seance`}
        </div>
        <div>
          <b>Nombre de séances:</b>
          {` ${sortedActivities.length}`}
        </div>
      </div>
      <Checkbox
        id={`input-days`}
        onChange={toggleShowDays}
        checked={showDays}
        className={styles.centeredInputLabel}
      >
        Show empty days
      </Checkbox>
      <HighchartsReact
        highcharts={Highcharts}
        options={options}
        ref={chartComponentRef}
        containerProps={{ className: styles.chartContainer }}
      />
      <Legend />
    </div>
  );
};

export default ActivityDayChart;
